import { useCallback, useEffect, useRef } from 'react';
import { listenForSmsCode, type SmsCodeListenerModel } from '../lib/smsRetriever';

export interface SmsCodeAutofillModel {
	/**
	 * Start listening for a code text, replacing any listener already running.
	 * Resolves the applicationId to send with the code request, or undefined
	 * when this is not an Android app that can fill the code in.
	 */
	listen: () => Promise<string | undefined>;
	/** Stop listening (the code was typed, the step was left, or the request failed). */
	stop: () => void;
}

/**
 * PD-17379 — fill a texted code in automatically on the Android storefront
 * app (see lib/smsRetriever). `onCode` is read when the text arrives, not when
 * listening starts, so it always sees the screen's current state. Listening
 * stops when the component unmounts.
 */
export function useSmsCodeAutofill(
	onCode: (code: string) => void,
): SmsCodeAutofillModel {
	const listenerRef = useRef<SmsCodeListenerModel | null>(null);
	const onCodeRef = useRef(onCode);
	onCodeRef.current = onCode;
	// Bumped on every listen and stop, so a listener that finishes starting
	// after it was replaced or cancelled stops itself instead of lingering.
	const generationRef = useRef(0);

	const stop = useCallback((): void => {
		generationRef.current += 1;
		listenerRef.current?.stop();
		listenerRef.current = null;
	}, []);

	const listen = useCallback(async (): Promise<string | undefined> => {
		stop();
		const generation = generationRef.current;
		const listener = await listenForSmsCode((code) => {
			onCodeRef.current(code);
		});
		if (listener == null) {
			return undefined;
		}
		if (generation !== generationRef.current) {
			listener.stop();
			return undefined;
		}
		listenerRef.current = listener;
		return listener.androidAppId;
	}, [stop]);

	useEffect(() => stop, [stop]);

	return { listen, stop };
}
